import { Button, IconButton, Tabs } from '@artosera/ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { OffrePresentee } from '../../api/offres.js';
import { useLangue } from '../../composants/useLangue.js';
import { useShellContext } from '../shell/context.js';
import { galerieALaction } from '../../auth/sujet.js';
import { NouvelleOffreDialog } from './NouvelleOffreDialog.js';
import { OffreCarte } from './OffreCarte.js';
import {
  contexteActionDeLaGalerie,
  segmentDepuisParametre,
  SEGMENTS_OFFRES,
  type SegmentOffres,
} from './syntheseOffre.js';
import { useVueOffres } from './vueOffres.js';
import styles from './OffresPage.module.css';
import { Verrouillable } from '../module/LectureSeule.js';

export function OffresPage() {
  const { t } = useTranslation(['regie', 'commun']);
  const langue = useLangue();
  const { galerieSlug, galerie } = useShellContext();
  const [params, setParams] = useSearchParams();
  const segment = segmentDepuisParametre(params.get('segment'));
  const [nouvelle, setNouvelle] = useState(false);
  const [page, setPage] = useState(1);
  const contexte = contexteActionDeLaGalerie(galerie);
  const vue = useVueOffres(galerieSlug, segment, page, contexte);

  const cartes = (offres: OffrePresentee[]) => (
    <ul className={styles.cartes}>
      {offres.map((offre) => (
        <li key={offre.id}>
          <OffreCarte offre={offre} galerieSlug={galerieSlug} langue={langue} contexte={contexte} />
        </li>
      ))}
    </ul>
  );
  const groupe = (cle: string, offres: OffrePresentee[]) =>
    offres.length ? (
      <section className={styles.groupe} aria-labelledby={`groupe-${cle}`}>
        <h2 id={`groupe-${cle}`}>
          {t(`regie:offres.groupes.${cle}`)}
          <span className={styles.compteur}>{offres.length}</span>
        </h2>
        <p className={styles.aide}>{t(`regie:offres.groupes.${cle}Aide`)}</p>
        {cartes(offres)}
      </section>
    ) : null;

  const pagination =
    vue.pages > 1 ? (
      <nav className={styles.pagination} aria-label={t('regie:offres.pagination')}>
        <IconButton
          variant="outline"
          aria-label={t('regie:offres.pagePrecedente')}
          icon={<ChevronLeft />}
          disabled={page <= 1}
          onClick={() => setPage(page - 1)}
        />
        <span>
          {page} / {vue.pages}
        </span>
        <IconButton
          variant="outline"
          aria-label={t('regie:offres.pageSuivante')}
          icon={<ChevronRight />}
          disabled={page >= vue.pages}
          onClick={() => setPage(page + 1)}
        />
      </nav>
    ) : null;

  const listeDuSegment = (cle: SegmentOffres): ReactNode => {
    if (vue.chargement) return <p role="status">{t('commun:chargement')}</p>;
    if (vue.erreur) return <p role="alert">{t('commun:erreurReseau')}</p>;
    if (cle === 'aTraiter') {
      const { brouillons, relances, acceptees } = vue.aTraiter;
      if (!brouillons.length && !relances.length && !acceptees.length)
        return <p className={styles.vide}>{t('regie:offres.rienATraiter')}</p>;
      return (
        <>
          {groupe('relances', relances)}
          {groupe('brouillons', brouillons)}
          {groupe('aFinaliser', acceptees)}
        </>
      );
    }
    const offres = vue[cle];
    if (!offres.length) return <p className={styles.vide}>{t('regie:offres.vide')}</p>;
    return (
      <>
        <p className={styles.aide}>{t(`regie:offres.segmentsAide.${cle}`)}</p>
        {cartes(offres)}
        {pagination}
      </>
    );
  };

  return (
    <section className={styles.page}>
      <header className={styles.entete}>
        <div>
          <h1>{t('regie:offres.titre')}</h1>
          <p className={styles.explication}>{t('regie:offres.explication')}</p>
        </div>
        <Verrouillable action="offers:write" ouvert={galerieALaction(galerie, 'offers:write')}>
          <Button onClick={() => setNouvelle(true)}>{t('regie:offres.nouvelle.titre')}</Button>
        </Verrouillable>
      </header>
      {nouvelle ? <NouvelleOffreDialog open onOpenChange={setNouvelle} /> : null}
      <Tabs
        value={segment}
        onValueChange={(value) => {
          setPage(1);
          setParams({ segment: value });
        }}
        tabs={SEGMENTS_OFFRES.map((cle) => ({
          value: cle,
          label: (
            <>
              {t(`regie:offres.segments.${cle}`)}
              {cle !== 'toutes' ? (
                <span className={styles.compteur}>{vue.compteurs[cle]}</span>
              ) : null}
            </>
          ),
          content: cle === segment ? listeDuSegment(cle) : null,
        }))}
      />
    </section>
  );
}
